import {
  CdkDrag,
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  inject,
  Injector,
  QueryList,
  runInInjectionContext,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { SiaCheckBoxComponent, SiaSelectBoxComponent } from '@sia/inputs';
import {
  jsonDeepClone,
  notify,
  retriveCurrentRouteData,
  SiaBaseActionService,
  SiaClickStopPropagation,
  SiaComponentService,
  SiaLayoutService,
  SiaNewRouterService,
  SiaRouteData,
  SiaSearchBoxComponent,
  SiaSessionService,
  SiaSignalrService,
  SiaTooltipDirective,
  SiaTranslatePipe,
} from '@sia/shared';
import { firstValueFrom, Subscription } from 'rxjs';
import { SiaActionPropertiesDialogComponent } from '../../dialogs/sia-action-properties-dialog/sia-action-properties-dialog.component';
import { SiaActionRenameDialogComponent } from '../../dialogs/sia-action-rename-dialog/sia-action-rename-dialog.component';
import { SiaActionBaseData } from '../../interfaces/sia-action-base-data';
import { SiaItemAction } from '../../interfaces/sia-item-action';
import { SiaItemWithActions } from '../../interfaces/sia-item-with-actions';
import { SiaActionsService } from '../../services/sia-actions.service';

@Component({
  selector: 'sia-actions-manager-page',
  templateUrl: './sia-actions-manager-page.component.html',
  styleUrls: ['./sia-actions-manager-page.component.scss'],
  standalone: true,
  imports: [
    CdkDropList,
    CdkDrag,
    SiaTranslatePipe,
    MatIcon,
    FormsModule,
    MatTab,
    MatTabGroup,
    NgTemplateOutlet,
    MatIconButton,
    MatMenuModule,
    SiaCheckBoxComponent,
    MatDivider,
    SiaSearchBoxComponent,
    SiaClickStopPropagation,
    SiaSelectBoxComponent,
    SiaTooltipDirective,
  ],
})
export class SiaActionsManagerPageComponent {
  #siaBaseActionService = inject(SiaBaseActionService);
  #siaComponentService = inject(SiaComponentService);
  #siaRouterService = inject(SiaNewRouterService);
  #siaActionsService = inject(SiaActionsService);
  #siaSessionService = inject(SiaSessionService);
  #siaSignalrService = inject(SiaSignalrService);
  #siaLayoutService = inject(SiaLayoutService);
  #matDialog = inject(MatDialog);
  #injector = inject(Injector);

  @ViewChildren(CdkDropList) dropLists?: QueryList<CdkDropList>;
  @ViewChild(MatMenuTrigger, { static: false }) matMenuTrigger!: MatMenuTrigger;

  private _components: SiaItemWithActions[] = [];
  private _filterGroupComp?: string | undefined;
  private _selectedActionsArrayIndex?: number;
  private _filterActions?: string | undefined;
  private _groups: SiaItemWithActions[] = [];
  private _actions: SiaActionBaseData[] = [];
  private _selectedActionsArray?: any[];
  private _subs: Subscription[] = [];

  selectedGroupOrComponentItem?: SiaItemWithActions;
  componentActionGroups: SiaItemWithActions[] = [];
  groupsOrComponentsPanelVisible: boolean = false;
  components: SiaItemWithActions[] = [];
  actionsList: SiaActionBaseData[] = [];
  selectedItemGroupActions: any = {};
  actionContextMenuX: number = 0;
  actionContextMenuY: number = 0;
  selectedItemActions: any = {};
  dropRequestIndex: number = 0;
  pageData: SiaRouteData = {};
  dragging: boolean = false;
  selectedAction: any;

  public get filterGroupComp(): string | undefined {
    return this._filterGroupComp;
  }

  public set filterGroupComp(value: string | undefined) {
    this._filterGroupComp = value?.toLowerCase();
    this.findComponentsAndGroups();
  }

  public get filterActions(): string | undefined {
    return this._filterActions;
  }

  public set filterActions(value: string | undefined) {
    this._filterActions = value?.toLowerCase();
    this.findActions();
  }

  manageActionFired(id: string | undefined) {
    switch (id) {
      case 'control-panel':
        this.groupsOrComponentsPanelVisible =
          !this.groupsOrComponentsPanelVisible;
        break;
      case 'save':
        this.saveActions();
        break;
      case 'new-action':
        this.#siaRouterService.navigateMenuWithParams(
          'popup^crud-actions',
          [],
          'create',
        );
        break;
      case 'new-group':
        this.#siaRouterService.navigateMenuWithParams(
          'popup^crud-action-groups',
          [],
          'create',
        );
        break;
    }
  }

  async loadData() {
    const currentComponentId =
      this.#siaSessionService.getSessionParamValue('componentid');
    const componentsAndGroups =
      await this.#siaActionsService.getItemWithActions();
    this.actionsList = (await this.#siaActionsService.getActions()) ?? [];
    this.actionsList.sort((a, b) => {
      if ((a.type ?? '') > (b.type ?? '')) return -1;
      if ((a.type ?? '') < (b.type ?? '')) return 1;
      if ((a.name ?? '') < (b.name ?? '')) return -1;
      if ((a.name ?? '') > (b.name ?? '')) return 1;
      return 0;
    });
    componentsAndGroups.sort((a, b) => {
      if ((a.description ?? '') < (b.description ?? '')) return -1;
      if ((a.description ?? '') > (b.description ?? '')) return 1;
      return (a.id ?? '') < (b.id ?? '') ? -1 : 1;
    });
    this.componentActionGroups = componentsAndGroups.filter(
      (item) => item.type === 'group',
    );
    this.components = componentsAndGroups.filter(
      (item) => item.type != 'group',
    );
    this._groups = jsonDeepClone(this.componentActionGroups);
    this._components = jsonDeepClone(this.components);
    this._actions = jsonDeepClone(this.actionsList);
    if (currentComponentId) {
      this.selectedGroupOrComponentItem = componentsAndGroups.find(
        (item) => item.id === currentComponentId,
      );
      if (this.selectedGroupOrComponentItem) {
        this.selectItem(
          this.selectedGroupOrComponentItem,
          this.selectedGroupOrComponentItem?.type == 'group'
            ? 'group'
            : 'component',
        );
      }
    } else {
      this.groupsOrComponentsPanelVisible = true;
    }
  }

  public findComponentsAndGroups() {
    this.components = this._components?.filter(
      (item) =>
        item.id?.toLowerCase().includes(this.filterGroupComp ?? '') ||
        item.description?.toLowerCase().includes(this.filterGroupComp ?? ''),
    );
    this.componentActionGroups = this._groups?.filter(
      (item) =>
        item.id?.toLowerCase().includes(this.filterGroupComp ?? '') ||
        item.description?.toLowerCase().includes(this.filterGroupComp ?? ''),
    );
  }

  public findActions() {
    this.actionsList = this._actions?.filter(
      (item) =>
        item.description?.toLowerCase().includes(this.filterActions ?? '') ||
        item.id?.toLowerCase().includes(this.filterActions ?? ''),
    );
  }

  closePanel() {
    if (this.selectedGroupOrComponentItem) {
      this.groupsOrComponentsPanelVisible = false;
    }
  }
  async selectItem(selectedItem: SiaItemWithActions, type: string) {
    if (!selectedItem.id) {
      return;
    }
    this.#siaLayoutService.showAdditionalTitle(
      selectedItem?.description,
      this.pageData.guid ?? '',
    );
    this.#siaSessionService.updateSessionParams('', [
      { id: 'componentid', value: selectedItem.id },
    ]);
    this.groupsOrComponentsPanelVisible = false;
    this.selectedGroupOrComponentItem = selectedItem;
    let componentActions =
      await this.#siaActionsService.getActionsByItemAndType(
        selectedItem.id,
        type,
      );
    componentActions.sort((a, z) => (a.orderIndex ?? 0) - (z.orderIndex ?? 0));

    this.selectedItemActions = {};
    this.selectedItemGroupActions = {};

    for (let action of componentActions) {
      if (!this.selectedItemActions[action.modeBeforeFire ?? 'read']) {
        this.selectedItemActions[action.modeBeforeFire ?? 'read'] = [];
        this.selectedItemGroupActions[action.modeBeforeFire ?? 'read'] = {};
      }
      if (action.actionGroupId) {
        if (
          !this.selectedItemGroupActions[action.modeBeforeFire ?? 'read'][
            action.actionGroupId
          ]
        ) {
          this.selectedItemGroupActions[action.modeBeforeFire ?? 'read'][
            action.actionGroupId
          ] = [];
        }
        this.selectedItemGroupActions[action.modeBeforeFire ?? 'read'][
          action.actionGroupId
        ].push(action);
      } else {
        this.selectedItemActions[action.modeBeforeFire ?? 'read'].push(action);
      }
    }

    componentActions
      .filter((x) => x.actionGroupId && x.componentActionGroupId)
      .forEach((action) => {
        let groupAction = this.selectedItemActions[
          action.modeBeforeFire ?? 'read'
        ].find((x: any) => x.id == action.actionGroupId);
        if (groupAction) {
          groupAction.componentActionGroupId = action.componentActionGroupId;
        }
      });
  }

  moveItem(event: any) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }

  getActionForModeElementsList():
    | string
    | CdkDropList<any>
    | (string | CdkDropList<any>)[] {
    this.dropRequestIndex++;
    return (
      this.dropLists?.filter((dropList) =>
        (dropList.element.nativeElement as HTMLElement).classList.contains(
          'actions-mode-list',
        ),
      ) ?? []
    );
  }

  getActionForGroupElementsList():
    | string
    | CdkDropList<any>
    | (string | CdkDropList<any>)[] {
    this.dropRequestIndex++;
    return (
      this.dropLists?.filter((dropList) =>
        (dropList.element.nativeElement as HTMLElement).classList.contains(
          'group-actions-list',
        ),
      ) ?? []
    );
  }

  async openActionProperties(action: SiaItemAction) {
    if (
      this.dragging ||
      (this.selectedGroupOrComponentItem?.type != 'group' &&
        action.componentActionGroupId)
    ) {
      return;
    }
    const dialogRef = this.#matDialog.open(SiaActionPropertiesDialogComponent, {
      width: '50vw', // Use viewport units instead of percentage
      minWidth: '400px', // Add minimum width
      maxWidth: '1000px', // Add maximum width
      panelClass: 'custom-dialog', // Add custom class for styling
      autoFocus: false,
      disableClose: true,
      data: { ...action },
    });
    let actionUpdated = await firstValueFrom(dialogRef.afterClosed());
    if (actionUpdated) {
      Object.assign(action, actionUpdated);
    }
  }

  addAction(
    action: SiaActionBaseData,
    actionsList: any[],
    mode: string,
    group?: string,
  ) {
    if (actionsList && actionsList.length > 0) {
      if (actionsList.find((item: SiaItemAction) => item.id === action.id)) {
        notify('Azione già presente', 2000, 'error');
        return;
      }
    }
    const newAction: SiaItemAction = {
      ...action,
      actionType: action.type == 'group' ? 'group' : 'action',
      modeBeforeFire: mode,
      actionGroupId: group,
    };
    if (action.type == 'group') {
      if (!this.selectedItemGroupActions[mode]?.[action.id ?? '']) {
        this.selectedItemGroupActions[mode] = {};
        this.selectedItemGroupActions[mode][action.id ?? ''] = [];
      }
    }
    !this.selectedItemActions[mode]
      ? (this.selectedItemActions[mode] = [])
      : '';
    this.selectedItemActions[mode].push(newAction);
  }

  openContextMenu(event: MouseEvent, actionsArray: any[], actionIndex: number) {
    this.actionContextMenuX = event.clientX;
    this.actionContextMenuY = event.clientY;
    this.matMenuTrigger.openMenu();
    event.preventDefault();
    event.stopPropagation();
    this._selectedActionsArray = actionsArray;
    this._selectedActionsArrayIndex = actionIndex;
    this.selectedAction =
      this._selectedActionsArray![this._selectedActionsArrayIndex!];
  }

  deleteSelectedAction() {
    this._selectedActionsArray!.splice(this._selectedActionsArrayIndex!, 1);
  }

  async openRenameDialog() {
    let actionsForMode: { mode: string; actions: SiaItemAction[] }[] = [
      { mode: 'update', actions: this.selectedItemActions['update'] },
      { mode: 'read', actions: this.selectedItemActions['read'] },
      { mode: 'create', actions: this.selectedItemActions['create'] },
    ];
    let name = this.selectedAction.specificName ?? this.selectedAction.name;
    let dialogRef = this.#matDialog.open(SiaActionRenameDialogComponent, {
      data: name,
    });
    let renameValue = await firstValueFrom(dialogRef.afterClosed());
    if (renameValue && renameValue != name) {
      this._selectedActionsArray![
        this._selectedActionsArrayIndex!
      ].specificName = renameValue;
      this._selectedActionsArray![
        this._selectedActionsArrayIndex!
      ].renamedByGroup = false;
    }
    actionsForMode.forEach((modeAndActions) => {
      modeAndActions.actions?.forEach((action) => {
        if (action.actionType == 'group') {
          this.selectedItemGroupActions[modeAndActions.mode][
            action.id!
          ].forEach((groupAction: SiaItemAction) => {
            if (this.selectedAction.id == groupAction.id) {
              groupAction.specificName = renameValue;
            }
          });
        } else {
          if (this.selectedAction.id == action.id) {
            action.specificName = renameValue;
          }
        }
      });
    });
  }
  resetName() {
    this.selectedAction.specificName = undefined;
    let actionsForMode: { mode: string; actions: SiaItemAction[] }[] = [
      { mode: 'update', actions: this.selectedItemActions['update'] },
      { mode: 'read', actions: this.selectedItemActions['read'] },
      { mode: 'create', actions: this.selectedItemActions['create'] },
    ];
    actionsForMode.forEach((modeAndActions) => {
      modeAndActions.actions?.forEach((action) => {
        if (action.actionType == 'group') {
          this.selectedItemGroupActions[modeAndActions.mode][
            action.id!
          ].forEach((groupAction: SiaItemAction) => {
            if (this.selectedAction.id == groupAction.id) {
              groupAction.specificName = undefined;
            }
          });
        } else {
          if (this.selectedAction.id == action.id) {
            action.specificName = undefined;
          }
        }
      });
    });
  }

  async saveActions() {
    if (!this.selectedGroupOrComponentItem?.id) {
      return;
    }

    const actionsForMode: { mode: string; actions: SiaItemAction[] }[] = [
      { mode: 'update', actions: this.selectedItemActions['update'] ?? [] },
      { mode: 'read', actions: this.selectedItemActions['read'] ?? [] },
      { mode: 'create', actions: this.selectedItemActions['create'] ?? [] },
    ];

    let allActions: SiaItemAction[] = [];
    let allActionsRenamed: SiaItemAction[] = [];

    if (this.selectedGroupOrComponentItem?.type == 'group') {
      actionsForMode.forEach((modeAndActions) => {
        if (modeAndActions.actions) {
          let indexIncrement = 0;
          modeAndActions.actions?.forEach((action) => {
            indexIncrement += 100;
            action.orderIndex = indexIncrement;
            if (action.actionType == 'group') {
              this.selectedItemGroupActions[modeAndActions.mode][
                action.id!
              ].forEach((groupAction: SiaItemAction) => {
                indexIncrement += 100;
                groupAction.orderIndex = indexIncrement;
                allActions.push(groupAction);
              });
            } else {
              allActions.push(action);
            }
          });
        }
      });
    } else {
      actionsForMode.forEach((modeAndActions) => {
        let indexIncrement = 0;
        modeAndActions.actions?.forEach((action) => {
          if (action.componentActionGroupId) {
            indexIncrement = action.orderIndex ?? 0;
            if (action.actionType == 'group') {
              this.selectedItemGroupActions[modeAndActions.mode][
                action.id!
              ].forEach((groupAction: SiaItemAction) => {
                if (groupAction.componentActionGroupId) {
                  indexIncrement = groupAction.orderIndex ?? 0;
                } else {
                  indexIncrement += 1;
                  groupAction.orderIndex = indexIncrement;
                  allActions.push(groupAction);
                }
              });
            }
          } else {
            indexIncrement += 1;
            if (action.actionType == 'group') {
              this.selectedItemGroupActions[modeAndActions.mode][
                action.id!
              ].forEach((groupAction: SiaItemAction) => {
                indexIncrement += 1;
                groupAction.orderIndex = indexIncrement;
                allActions.push(groupAction);
              });
            } else {
              action.orderIndex = indexIncrement;
              allActions.push(action);
            }
          }
        });
      });
    }

    actionsForMode.forEach((modeAndActions) => {
      if (modeAndActions.actions) {
        modeAndActions.actions?.forEach((action) => {
          if (action.actionType == 'group') {
            this.selectedItemGroupActions[modeAndActions.mode][
              action.id!
            ].forEach((groupAction: SiaItemAction) => {
              if (
                groupAction.specificName &&
                groupAction.specificName != groupAction.name &&
                (this.selectedGroupOrComponentItem?.type == 'group' ||
                  groupAction.renamedByGroup == false)
              ) {
                if (
                  allActionsRenamed.findIndex((x) => x.id == groupAction.id) ==
                  -1
                ) {
                  allActionsRenamed.push({
                    id: groupAction.id,
                    name: groupAction.specificName,
                  });
                }
              }
            });
          } else {
            if (
              action.specificName &&
              action.specificName != action.name &&
              (this.selectedGroupOrComponentItem?.type == 'group' ||
                action.renamedByGroup == false)
            ) {
              if (allActionsRenamed.findIndex((x) => x.id == action.id) == -1) {
                allActionsRenamed.push({
                  id: action.id,
                  name: action.specificName,
                });
              }
            }
          }
        });
      }
    });
    var actionSaved = await this.#siaActionsService.saveActions(
      allActions,
      this.selectedGroupOrComponentItem!.id!,
      this.selectedGroupOrComponentItem?.type == 'group',
    );

    let actionRenameSaved = await this.#siaActionsService.renameActions(
      allActionsRenamed,
      this.selectedGroupOrComponentItem!.id!,
      this.selectedGroupOrComponentItem?.type == 'group',
    );

    if (actionSaved) {
      notify('Azioni salvate', 2000, 'success');
    } else if (actionRenameSaved) {
      notify('Azioni salvate e rinominate', 2000, 'success');
    }

    this.selectItem(
      this.selectedGroupOrComponentItem,
      this.selectedGroupOrComponentItem?.type == 'group'
        ? 'group'
        : 'component',
    );
    this.loadData();
  }

  modifyActions() {
    this.#siaRouterService.navigateMenuWithParams('popup^crud-actions', [
      { id: 'id', value: this.selectedAction.id },
    ]);
  }

  updateSelectedItemGroup() {
    if (!this.selectedGroupOrComponentItem) {
      return;
    }
    this.#siaComponentService
      .updateActionGroupId(
        this.selectedGroupOrComponentItem.id!,
        this.selectedGroupOrComponentItem.actionGroupId ?? null,
      )
      .then((data: boolean) => {
        if (!data) {
          notify('Impossibile cambiare gruppo action', 2000, 'error');
          return;
        }
        this.selectItem(
          this.selectedGroupOrComponentItem!,
          this.selectedGroupOrComponentItem?.type == 'group'
            ? 'group'
            : 'component',
        );
      });
  }

  ngOnInit() {
    let s = this.#siaActionsService.actionFired$().subscribe((action) => {
      this.manageActionFired(action.action?.id);
    });
    this._subs.push(s);
    s = this.#siaRouterService.routeChanged$.subscribe(() => {
      var pageData: any = null;
      runInInjectionContext(this.#injector, () => {
        pageData = retriveCurrentRouteData() ?? {};
      });
      if (!pageData?.guid || pageData?.guid == this.pageData?.guid) {
        return;
      }
      this.pageData = pageData!;
      this.#siaBaseActionService.handleAction(
        this.pageData.componentId ?? '',
        this.pageData.guid ?? '',
        'refresh',
        false,
      );
      this.#siaBaseActionService.handleAction(
        this.pageData.componentId ?? '',
        this.pageData.guid ?? '',
        'import',
        false,
      );
      this.loadData();
      this.#siaLayoutService.showAdditionalTitle(
        this.selectedGroupOrComponentItem?.description,
        this.pageData.guid ?? '',
      );
    });
    this._subs.push(s);
    s = this.#siaSignalrService.refreshMessage$.subscribe((message) => {
      if ((message && message['action-groups']) || message['*']) {
        this.loadData();
      }
    });
    this._subs.push(s);
  }
  ngOnDestroy() {
    this._subs?.forEach((sub) => sub.unsubscribe());
  }
}
